import React, { useCallback, useContext, useEffect, useState } from 'react'
import './styles/global.scss'
import { ConfigContext } from './store/ConfigContext'
import useBetaAccessCodeValidation from './hooks/useBetaAccessCodeValidation'
import { UserContext } from './store/UserContext'
import ProgressIndicator from '@veneer/core/dist/esm/scripts/progress_indicator'
import { ContentStackTypes, FeatureFlagKeys, Paths } from './store/Constants'
import {
  DeltaCodePage,
  NotFoundPage,
  SidebarOptionPage
} from './pages/LazyPages'
import { ContentStackContext } from '@jarvis/olex-content-management'
import { FlowRoutes } from './routes/routes'
import { SuspenseWrapper } from './store/SuspenseWrapper'
import { AllModals } from './modals/AllModals'

const App = () => {
  const {
    header,
    flags,
    isLoaded,
    printerFamily,
    currentPath,
    flowActions,
    pathsThatDoesntNeedSku,
    setDeltaPageIsShowing
  } = useContext(ConfigContext)

  const { deltaCode, loadedFromStore } = useContext(UserContext)
  const { livePreviewInfo } = useContext(ContentStackContext)
  const {
    [FeatureFlagKeys.betaAccessCode]: deltaCodeFlag,
    [FeatureFlagKeys.saltString]: saltString,
    [FeatureFlagKeys.deltaPageDerivatives]: deltaPageDerivatives
  } = flags || {}
  const [userVerifiedByCache, setUserVerifiedByCache] = useState(false)
  const { codeInvalid, validated, setValidated, validate } =
    useBetaAccessCodeValidation()

  const deltaCodeSubmissionHandler = useCallback(
    (validity) => {
      setUserVerifiedByCache(true)
      setValidated(validity)
    },
    [setValidated]
  )

  useEffect(() => {
    if (!deltaCodeFlag) {
      setUserVerifiedByCache(true)
    }
  }, [deltaCodeFlag])

  useEffect(() => {
    if (deltaCodeFlag && loadedFromStore && deltaCode && !userVerifiedByCache) {
      validate(deltaCode, saltString, deltaCodeFlag, deltaCodeSubmissionHandler)
    }
  }, [
    deltaCodeFlag,
    loadedFromStore,
    deltaCode,
    userVerifiedByCache,
    validate,
    deltaCodeSubmissionHandler,
    saltString,
    deltaPageDerivatives
  ])

  const showDeltaPage =
    deltaCodeFlag &&
    deltaPageDerivatives?.includes(printerFamily) &&
    (!deltaCode || (userVerifiedByCache && validated && codeInvalid))

  useEffect(() => {
    setDeltaPageIsShowing?.(!!showDeltaPage)
  }, [showDeltaPage])

  /**
   * Loop through the conditions and check if any of the conditions are true, otherwise they fall into the default rendering.
   * @type {[{condition: boolean, component: React.JSX.Element}]}
   */
  const conditionToRenderComponentMap = [
    {
      condition: !isLoaded && !pathsThatDoesntNeedSku.includes(currentPath),
      component: (
        <ProgressIndicator behavior={'indeterminate'} appearance="linear" />
      )
    },
    {
      condition:
        showDeltaPage ||
        livePreviewInfo?.contentTypeUid ===
          ContentStackTypes.delta_access_page.content_type_uid,
      component: (
        <DeltaCodePage
          salt={saltString}
          result={deltaCodeFlag}
          onCodeSubmit={deltaCodeSubmissionHandler}
        />
      )
    },
    {
      condition: currentPath === Paths.not_found,
      component: <NotFoundPage />
    },
    {
      condition: header.selectedTab > 0,
      component: <SidebarOptionPage />
    }
  ]

  const componentToRender = conditionToRenderComponentMap.find(
    ({ condition }) => condition
  )?.component

  if (componentToRender) {
    return (
      <SuspenseWrapper>
        <AllModals />
        {componentToRender}
      </SuspenseWrapper>
    )
  }

  if (flowActions.status !== 'DONE') {
    return <></>
  }

  return (
    <SuspenseWrapper>
      <AllModals />
      {FlowRoutes?.[flowActions?.currentFlow?.page_id] ||
        FlowRoutes['not-found']}
    </SuspenseWrapper>
  )
}

export default App
